import { ChangeEvent, useState, useId } from "react";
import clsx from "clsx";
import { Button, Col } from "@/components";
import { Close } from "@/icons";
import "./SearchGeneric.scss";
import { useMediaQuery } from "@/hooks/useMediaQuery.ts";

export type SearchGenericProps<T extends object> = {
  btnText: string;
  helperText: string;
  instructionsText?: string;
  mobileHelperText?: string;
  value: string;
  items: T[];
  setValue: (value: string) => void;
  setTypedValue?: (value: string) => void;
  getSearchField: (item: T) => string;
  getDisplayField?: (item: T) => string;
  onItemSelect?: (item: T) => void;
  minCharsToSearch?: number;
  className?: string;
  dimensionStrategy?: "fixed" | "auto";
};

export function SearchGeneric<T extends object>({
  btnText,
  helperText,
  instructionsText = "Inserisci almeno $minCharsToSearch caratteri per iniziare la ricerca.",
  mobileHelperText = helperText,
  value,
  items,
  setValue,
  setTypedValue,
  getSearchField,
  getDisplayField = getSearchField,
  onItemSelect,
  minCharsToSearch = 3,
  className,
  dimensionStrategy = "fixed",
}: SearchGenericProps<T>) {
  const [valueInput, setValueInput] = useState<string>(value || "");
  const [filteredItems, setFilteredItems] = useState<T[]>([]);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  
  // Generate unique IDs for accessibility
  const searchId = useId();
  const listboxId = `${searchId}-listbox`;
  const instructionsId = `${searchId}-instructions`;
  const labelId = `${searchId}-label`;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValueInput(inputValue);
    if (setTypedValue) {
      setTypedValue(inputValue);
    }
    if (inputValue.length >= minCharsToSearch) {
      const results = items.filter((item) => {
        const searchField = getSearchField(item);
        return searchField?.toLowerCase().includes(inputValue.toLowerCase());
      });
      setFilteredItems(results);
    } else {
      setFilteredItems([]);
    }
  };

  const handleSelectItem = (item: T) => {
    const displayValue = getDisplayField(item);
    setValueInput(displayValue);
    setValue(displayValue);
    setFilteredItems([]);

    if (onItemSelect) {
      onItemSelect(item);
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));

    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <span key={i} className="text-primary fw-bold">
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </>
    );
  };

  return (
    <div
      className={clsx(
        "ui-search",
        "br-x4 bg-border-secondary w-full",
        className,
        {
          "ui-search-generic-auto": dimensionStrategy === "auto",
        },
      )}
    >
      {/* Screen reader only label */}
      <label id={labelId} htmlFor={searchId} className="sr-only">
        {helperText}
      </label>
      
      {/* Instructions for screen readers */}
      {instructionsText && <div id={instructionsId} className="sr-only">
        {instructionsText?.replace("$minCharsToSearch", String(minCharsToSearch))}
      </div>}

      <div className="position-relative">
        <input
          id={searchId}
          type="text"
          role="combobox"
          aria-expanded={filteredItems.length > 0}
          aria-haspopup="listbox"
          aria-owns={filteredItems.length > 0 ? listboxId : undefined}
          aria-describedby={instructionsId}
          className={clsx(
            "ui-search-input",
            "fs-6 md-fs-custom text-slate-20 br-x4 w-full pl-x24",
            valueInput && valueInput.length >= 3 && "has-clear-icon",
          )}
          placeholder={isDesktop ? helperText : mobileHelperText}
          value={valueInput}
          onChange={handleInputChange}
          maxLength={30}
          autoComplete="off"
        />
        {valueInput && (
          <Close
            className="ui-clean-button"
            ariaLabel="Cancella ricerca"
            onClick={() => {
              setValueInput("");
              setFilteredItems([]);
              setValue("");
              handleInputChange({ target: { value: "" } } as any);
            }}
          />
        )}
      </div>
      {filteredItems.length > 0 && (
        <div 
          className="ui-wrap-list-item-search shadow position-absolute p-x24"
          role="listbox"
          id={listboxId}
          aria-label={`${filteredItems.length} risultati disponibili`}
        >
          <Col
            gap={"x24"}
            fw={"regular"}
            text={"secondary"}
            fs={"6"}
            className="ui-list-item-search overflow-y-auto "
          >
            {filteredItems.map((item, index) => (
              <div
                key={index}
                id={`${searchId}-option-${index}`}
                role="option"
                onClick={() => handleSelectItem(item)}
                className="hover:bg-primary"
              >
                {highlightMatch(getDisplayField(item), valueInput)}
              </div>
            ))}
          </Col>
        </div>
      )}
      
      {/* Live region for announcing search results */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {filteredItems.length > 0 && valueInput.length >= minCharsToSearch && 
          `${filteredItems.length} risultati trovati`}
        {valueInput.length >= minCharsToSearch && filteredItems.length === 0 && 
          "Nessun risultato trovato"}
      </div>
      <div className={clsx("ui-div-btn")}>
        <Button
          className={clsx(
            "ui-btn-search",
            "bg-primary text-white fs-7 md-fs-6 fw-semibold br-x4",
          )}
          onClick={() => {
            setFilteredItems([]);
            setValue(valueInput);
          }}
        >
          {btnText}
        </Button>
      </div>
    </div>
  );
}
